import { db, functions, auth } from '../firebase';
import { collection, doc, getDoc, getDocs, where } from 'firebase/firestore';
import { createListQuery } from './firebase';
import { httpsCallable } from 'firebase/functions';

// Cloud Functions
export const createNewRealityFunction = httpsCallable(functions, 'createNewReality');
export const removeRealityFunction = httpsCallable(functions, 'removeReality');
// Test function to check if httpsCallable works
export const ttsGenerateFunction = httpsCallable(functions, 'ttsGenerate');

// Test function to verify httpsCallable is working
export const testCloudFunction = async () => {
  try {
    console.log('Testing Cloud Function connection...');
    const currentUser = auth.currentUser;
    console.log('Current user:', currentUser ? 'Authenticated' : 'Not authenticated');

    // Try calling a simple function that we know exists
    const result = await ttsGenerateFunction({
      inputText: ['test'],
      voiceName: 'test',
      type: 'test'
    });

    console.log('Test function result:', result);
    return result;
  } catch (error) {
    console.error('Test function error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

// API functions for admin operations
export const createNewReality = async (userId, realityData) => {
  try {
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    console.log('Current user:', currentUser ? 'Authenticated' : 'Not authenticated');
    console.log('Calling createNewReality with data:', { userId, realityData });

    const result = await createNewRealityFunction({
      userId,
      lang: realityData.lang,
      realityTitle: realityData.title || `Reality ${Date.now()}`, // Generate a title if not provided
      prompts: {
        prompt1: realityData.prompt1,
        prompt2: realityData.prompt2
      },
      isPaid: realityData.isPaid || false,
      isAdsEnabled: false,
      voice: realityData.voice,
      voiceDisplayName: realityData.voiceDisplayName || realityData.voice,
      daysByReality: 30, // Default to 30 days
      createdBy: realityData.createdBy || 'admin',
      hypnosis: null
    });

    console.log('createNewReality result:', result);
    return result.data;
  } catch (error) {
    console.error('Error creating new reality:', error);
    console.error('Error details:', error.code, error.message);
    throw error;
  }
};

export const removeReality = async (userId, realityId) => {
  try {
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    console.log('Current user:', currentUser ? 'Authenticated' : 'Not authenticated');
    console.log('Calling removeReality with:', { userId, realityId });

    const result = await removeRealityFunction({
      userId,
      realityId
    });

    console.log('removeReality result:', result);
    return result.data;
  } catch (error) {
    console.error('Error removing reality:', error);
    console.error('Error details:', error.code, error.message);
    throw error;
  }
};

export const getUsers = async (params) => {
  const formattedFilters = Object.entries(params.filters || {}).map(([key, value]) => {
    if (key === 'freeDemoCompleted') {
      if (value === 'Yes') {
        return where(key, '==', true);
      } else if (value === 'No') {
        return where(key, '==', false);
      }
    } else if (key === 'shop.totalRealitiesAcquired' || key === 'shop.realitiesAvailable') {
      if (value === 'Yes') {
        return where(key, '>', 0);
      } else if (value === 'No') {
        return where(key, '==', 0);
      }
    } else if (key === 'currentReality') {
      if (value === 'Yes') {
        return where(key, '!=', '');
      } else if (value === 'No') {
        return where(key, '==', '');
      }
    } else if (Array.isArray(value)) {
      return where(key, 'in', value);
    } else {
      return where(key, '==', value);
    }
  });
  const { data, ...props } = await createListQuery('users', { ...params, filters: formattedFilters });

  // Loop through data and add currentRealityDay and currentRealityLastUpdate
  const updatedData = await Promise.all(data.map(async (user) => {
    if (user.currentReality) {
      const realityData = await getRealityData(user.id, user.currentReality);
      if (realityData) {
        return {
          ...user,
          currentRealityDay: realityData.currentDay,
          currentRealityLastUpdate: realityData.updatedAt,
        };
      }
    }
    return user;
  }));

  return {
    data: updatedData,
    ...props,
  };
};

export const getRealityData = async (userId, realityId) => {
  const realityRef = doc(db, 'users', userId, 'realities', realityId);
  const realitySnapshot = await getDoc(realityRef);

  if (realitySnapshot.exists()) {
    return realitySnapshot.data();
  } else {
    return null;
  }
};

export const getUserData = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userSnapshot = await getDoc(userRef);

  if (!userSnapshot.exists()) {
    return null;
  }

  const realitiesRef = collection(userRef, 'realities');
  const realitiesSnapshot = await getDocs(realitiesRef);
  const realities = realitiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const userData = { id: userSnapshot.id, ...userSnapshot.data(), realities };

  return userData;
};

export const createCoreSession = async (userId, realityId, dayId = null) => {
  try {
    const createCoreSessionFunction = httpsCallable(functions, 'createCoreSessionOnCall');
    
    // Get user data to extract userName and gender
    const userData = await getUserData(userId);
    if (!userData) throw new Error('User not found');
    
    const result = await createCoreSessionFunction({
      userId,
      realityId,
      userName: userData.displayName || userData.name || 'User',
      gender: userData.gender || 'neutral',
      dayId
    });
    
    return result.data;
  } catch (error) {
    console.error('Error creating core session:', error);
    throw error;
  }
};

export const createCraftedSession = async (userId, realityId, dayId = null, forceRegenerate = false) => {
  try {
    const createCraftedSessionFunction = httpsCallable(functions, 'createCraftedSessionOnCall');
    
    // Get user data to extract userName and gender
    const userData = await getUserData(userId);
    if (!userData) throw new Error('User not found');
    
    const result = await createCraftedSessionFunction({
      userId,
      realityId,
      userName: userData.displayName || userData.name || 'User',
      gender: userData.gender || 'neutral',
      dayId,
      forceRegenerate
    });
    
    return result.data;
  } catch (error) {
    console.error('Error creating crafted session:', error);
    throw error;
  }
};